/**
 * OmologaForm — campi del form omologa (parametri + ponti trasportatori/
 * intermediari con mini-modal nested "+ Nuovo").
 *
 * Estratto da OmologaModal (Blocco 6 STEP 4 sotto-commit 3) per essere riusato:
 *  - da OmologaModal (standalone, autosalvante);
 *  - dal wizard genera-preventivo-omologa (ramo "nuova", senza salvataggio:
 *    è il BE a creare l'omologa con la transizione).
 *
 * Estrazione puramente meccanica: l'istanza react-hook-form vive nel parent e
 * viene passata via props (register/control/errors/setValue/getValues), così il
 * comportamento di OmologaModal resta identico.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import type { OmologaUpdateData } from '../schemas/omologa';
import { createTrasportatore, listTrasportatori } from '../api/trasportatori';
import { createIntermediario, listIntermediari } from '../api/intermediari';
import type { Trasportatore } from '../types/trasportatore';
import type { Intermediario } from '../types/intermediario';
import {
  trasportatoreCreateSchema, type TrasportatoreCreateData,
} from '../schemas/trasportatore';
import {
  intermediarioCreateSchema, type IntermediarioCreateData,
} from '../schemas/intermediario';

export const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

export function FieldWrap({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="block text-xs text-ink-soft mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-accent-danger mt-0.5">{error}</p>}
    </div>
  );
}

// ── Autocomplete + chip multi-select + pulsante "+ Nuovo" ────────────────────

interface PickerItem {
  id: string;
  ragione_sociale: string;
  numero_iscrizione_anga: string;
}

function AnagraficaPicker({
  label,
  selectedIds,
  onChange,
  search,
  onCreateNew,
  refreshKey,
}: {
  label: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  search: (q: string) => Promise<PickerItem[]>;
  onCreateNew: () => void;
  refreshKey: number;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PickerItem[]>([]);
  const [selectedMap, setSelectedMap] = useState<Record<string, PickerItem>>({});
  const [open, setOpen] = useState(false);

  // Chiave primitiva stabile: evita refetch loop dovuti all'identità dell'array
  // selectedIds (nuovo ref a ogni render dal Controller RHF). L'effetto lavora
  // sulla chiave e ricostruisce gli id da essa → nessuna dipendenza mancante.
  const selectedKey = selectedIds.join('|');

  useEffect(() => {
    void (async () => {
      const ids = selectedKey ? selectedKey.split('|') : [];
      if (ids.length === 0) {
        setSelectedMap({});
        return;
      }
      try {
        const all = await search('');
        const map: Record<string, PickerItem> = {};
        for (const item of all) {
          if (ids.includes(item.id)) map[item.id] = item;
        }
        setSelectedMap(map);
      } catch {
        /* ignore */
      }
    })();
  }, [selectedKey, refreshKey, search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!query.trim()) {
        setResults([]);
        return;
      }
      search(query).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, refreshKey, search]);

  function add(item: PickerItem) {
    if (selectedIds.includes(item.id)) return;
    onChange([...selectedIds, item.id]);
    setSelectedMap({ ...selectedMap, [item.id]: item });
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  function remove(id: string) {
    onChange(selectedIds.filter((x) => x !== id));
  }

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs text-ink-soft">{label}</label>
        <button type="button" onClick={onCreateNew}
          className="flex items-center gap-1 text-xs text-sage hover:text-sage-dark font-medium">
          <Plus size={12} />{t('richieste.omologa_form.picker_nuovo')}
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {selectedIds.map((sid) => {
          const item = selectedMap[sid];
          return (
            <span key={sid} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-sage-light text-sage-dark border border-[#c5d0c2] rounded">
              {item ? `${item.ragione_sociale} · ${item.numero_iscrizione_anga}` : sid.slice(0, 8)}
              <button type="button" onClick={() => remove(sid)} className="hover:text-accent-danger">
                <X size={10} />
              </button>
            </span>
          );
        })}
      </div>

      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t('richieste.omologa_form.picker_search_placeholder')}
          className={inputCls}
        />
        {open && results.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-stone-border rounded-md shadow-lg max-h-48 overflow-y-auto">
            {results.map((r) => (
              <button key={r.id} type="button" onClick={() => add(r)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-stone-bg flex items-center justify-between">
                <span>{r.ragione_sociale}</span>
                <span className="text-xs font-mono text-ink-soft">{r.numero_iscrizione_anga}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Mini-modal nested: crea nuovo trasportatore ──────────────────────────────

function CreaTrasportatoreInlineModal({
  onClose, onCreated,
}: {
  onClose: () => void;
  onCreated: (tras: Trasportatore) => void;
}) {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const { register, handleSubmit, setError, formState: { errors } } =
    useForm<TrasportatoreCreateData>({
      resolver: zodResolver(trasportatoreCreateSchema),
      defaultValues: {
        ragione_sociale: '', numero_iscrizione_anga: '', data_rilascio: '',
        categorie_classe: '', piva: '', referente: '', contatti: '',
      },
    });

  const onSubmit = async (d: TrasportatoreCreateData) => {
    setIsSaving(true);
    setGlobalError(null);
    try {
      const created = await createTrasportatore({
        ragione_sociale: d.ragione_sociale,
        numero_iscrizione_anga: d.numero_iscrizione_anga,
        data_rilascio: d.data_rilascio,
        categorie_classe: d.categorie_classe,
        piva: d.piva?.trim() || null,
        referente: d.referente?.trim() || null,
        contatti: d.contatti?.trim() || null,
      });
      onCreated(created);
    } catch (err: unknown) {
      const s = (err as { response?: { status?: number } })?.response?.status;
      if (s === 409) setError('piva', { message: t('richieste.omologa_form.piva_esistente') });
      else setGlobalError(t('richieste.omologa_form.errore_salvataggio'));
    } finally {
      setIsSaving(false);
    }
  };

  // z-60 sopra il modal omologa (z-50). ESC chiude solo questo mini-modal.
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="modal-nuovo-trasportatore">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-mid">
          <h3 className="text-sm font-semibold text-ink-dark">{t('richieste.omologa_form.tras_titolo')}</h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="px-5 py-4 overflow-y-auto">
          {globalError && (
            <div className="mb-3 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded-md text-sm text-accent-danger">{globalError}</div>
          )}
          <FieldWrap label={t('richieste.omologa_form.ragione_sociale')} error={errors.ragione_sociale?.message}>
            <input className={inputCls} {...register('ragione_sociale')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.anga')} error={errors.numero_iscrizione_anga?.message}>
            <input className={inputCls} {...register('numero_iscrizione_anga')} placeholder={t('richieste.omologa_form.anga_ph_tras')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.data_rilascio')} error={errors.data_rilascio?.message}>
            <input className={inputCls} type="date" {...register('data_rilascio')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.categorie_classe')} error={errors.categorie_classe?.message}>
            <input className={inputCls} {...register('categorie_classe')} placeholder={t('richieste.omologa_form.cat_ph_tras')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.partita_iva')} error={errors.piva?.message}>
            <input className={inputCls} {...register('piva')} maxLength={11} placeholder={t('richieste.omologa_form.piva_ph')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.referente')} error={errors.referente?.message}>
            <input className={inputCls} {...register('referente')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.contatti')} error={errors.contatti?.message}>
            <input className={inputCls} {...register('contatti')} />
          </FieldWrap>
          <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-stone-mid">
            <button type="button" onClick={onClose} disabled={isSaving}
              className="px-3 py-1.5 text-xs text-ink-mid border border-stone-border rounded-md hover:bg-stone-bg transition-colors disabled:opacity-50">
              {t('common.azioni.annulla')}
            </button>
            <button type="submit" disabled={isSaving}
              className="px-4 py-1.5 text-xs font-medium text-white bg-sage rounded-md hover:bg-sage-dark transition-colors disabled:opacity-60">
              {isSaving ? t('richieste.omologa_form.creazione_in_corso') : t('richieste.omologa_form.crea_trasportatore')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Mini-modal nested: crea nuovo intermediario ──────────────────────────────

function CreaIntermediarioInlineModal({
  onClose, onCreated,
}: {
  onClose: () => void;
  onCreated: (i: Intermediario) => void;
}) {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const { register, handleSubmit, setError, formState: { errors } } =
    useForm<IntermediarioCreateData>({
      resolver: zodResolver(intermediarioCreateSchema),
      defaultValues: {
        ragione_sociale: '', numero_iscrizione_anga: '', data_rilascio: '',
        categorie_classe: '', piva: '', referente: '', contatti: '',
      },
    });

  const onSubmit = async (d: IntermediarioCreateData) => {
    setIsSaving(true);
    setGlobalError(null);
    try {
      const created = await createIntermediario({
        ragione_sociale: d.ragione_sociale,
        numero_iscrizione_anga: d.numero_iscrizione_anga,
        data_rilascio: d.data_rilascio,
        categorie_classe: d.categorie_classe,
        piva: d.piva?.trim() || null,
        referente: d.referente?.trim() || null,
        contatti: d.contatti?.trim() || null,
      });
      onCreated(created);
    } catch (err: unknown) {
      const s = (err as { response?: { status?: number } })?.response?.status;
      if (s === 409) setError('piva', { message: t('richieste.omologa_form.piva_esistente') });
      else setGlobalError(t('richieste.omologa_form.errore_salvataggio'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="modal-nuovo-intermediario">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-mid">
          <h3 className="text-sm font-semibold text-ink-dark">{t('richieste.omologa_form.int_titolo')}</h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="px-5 py-4 overflow-y-auto">
          {globalError && (
            <div className="mb-3 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded-md text-sm text-accent-danger">{globalError}</div>
          )}
          <FieldWrap label={t('richieste.omologa_form.ragione_sociale')} error={errors.ragione_sociale?.message}>
            <input className={inputCls} {...register('ragione_sociale')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.anga')} error={errors.numero_iscrizione_anga?.message}>
            <input className={inputCls} {...register('numero_iscrizione_anga')} placeholder={t('richieste.omologa_form.anga_ph_int')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.data_rilascio')} error={errors.data_rilascio?.message}>
            <input className={inputCls} type="date" {...register('data_rilascio')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.categorie_classe')} error={errors.categorie_classe?.message}>
            <input className={inputCls} {...register('categorie_classe')} placeholder={t('richieste.omologa_form.cat_ph_int')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.partita_iva')} error={errors.piva?.message}>
            <input className={inputCls} {...register('piva')} maxLength={11} placeholder={t('richieste.omologa_form.piva_ph')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.referente')} error={errors.referente?.message}>
            <input className={inputCls} {...register('referente')} />
          </FieldWrap>
          <FieldWrap label={t('richieste.omologa_form.contatti')} error={errors.contatti?.message}>
            <input className={inputCls} {...register('contatti')} />
          </FieldWrap>
          <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-stone-mid">
            <button type="button" onClick={onClose} disabled={isSaving}
              className="px-3 py-1.5 text-xs text-ink-mid border border-stone-border rounded-md hover:bg-stone-bg transition-colors disabled:opacity-50">
              {t('common.azioni.annulla')}
            </button>
            <button type="submit" disabled={isSaving}
              className="px-4 py-1.5 text-xs font-medium text-white bg-sage rounded-md hover:bg-sage-dark transition-colors disabled:opacity-60">
              {isSaving ? t('richieste.omologa_form.creazione_in_corso') : t('richieste.omologa_form.crea_intermediario')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Form omologa (campi condivisi) ───────────────────────────────────────────

// Tipizzato su OmologaUpdateData (campi omologa senza `produttore_id`): è il
// set di campi effettivamente reso qui. Entrambi i consumer — OmologaModal e il
// wizard genera-preventivo-omologa — usano lo stesso schema; il produttore non
// è un campo del form (lo passa il chiamante / lo deriva il BE).
interface OmologaFieldsProps {
  register: UseFormRegister<OmologaUpdateData>;
  control: Control<OmologaUpdateData>;
  errors: FieldErrors<OmologaUpdateData>;
  setValue: UseFormSetValue<OmologaUpdateData>;
  getValues: UseFormGetValues<OmologaUpdateData>;
}

export default function OmologaFields({
  register, control, errors, setValue, getValues,
}: OmologaFieldsProps) {
  const { t } = useTranslation();
  const [pickerRefresh, setPickerRefresh] = useState(0);
  const [showCreaTras, setShowCreaTras] = useState(false);
  const [showCreaInt, setShowCreaInt] = useState(false);

  // useCallback: identità stabile richiesta dagli effetti di AnagraficaPicker.
  const searchTras = useCallback(async (q: string) => {
    const res = await listTrasportatori({ limit: 25, ...(q ? { search: q } : {}) });
    return res.items.map((tras: Trasportatore) => ({
      id: tras.id, ragione_sociale: tras.ragione_sociale, numero_iscrizione_anga: tras.numero_iscrizione_anga,
    }));
  }, []);
  const searchInt = useCallback(async (q: string) => {
    const res = await listIntermediari({ limit: 25, ...(q ? { search: q } : {}) });
    return res.items.map((i: Intermediario) => ({
      id: i.id, ragione_sociale: i.ragione_sociale, numero_iscrizione_anga: i.numero_iscrizione_anga,
    }));
  }, []);

  function handleTrasportatoreCreated(tras: Trasportatore) {
    const current = (getValues('trasportatori_ids') as string[] | undefined) ?? [];
    setValue('trasportatori_ids', [...current, tras.id], { shouldDirty: true });
    setPickerRefresh((k) => k + 1);
    setShowCreaTras(false);
  }
  function handleIntermediarioCreated(i: Intermediario) {
    const current = (getValues('intermediari_ids') as string[] | undefined) ?? [];
    setValue('intermediari_ids', [...current, i.id], { shouldDirty: true });
    setPickerRefresh((k) => k + 1);
    setShowCreaInt(false);
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <FieldWrap label={t('richieste.omologa_form.numero_conferimenti')} error={errors.numero_conferimenti?.message as string | undefined}>
          <input className={inputCls} type="number" min={1}
            {...register('numero_conferimenti', { setValueAs: (v) => v === '' || v == null ? null : Number(v) })} />
        </FieldWrap>
        <FieldWrap label={t('richieste.omologa_form.frequenza')} error={errors.frequenza?.message}>
          <input className={inputCls} {...register('frequenza')} placeholder={t('richieste.omologa_form.frequenza_ph')} />
        </FieldWrap>
        <FieldWrap label={t('richieste.omologa_form.pezzatura')} error={errors.pezzatura?.message}>
          <input className={inputCls} {...register('pezzatura')} />
        </FieldWrap>
        <FieldWrap label={t('richieste.omologa_form.data_firma')} error={errors.data_firma?.message}>
          <input className={inputCls} type="date" {...register('data_firma')} data-testid="input-data-firma" />
        </FieldWrap>
        <FieldWrap label={t('richieste.omologa_form.data_scadenza')} error={errors.data_scadenza?.message}>
          <input className={inputCls} type="date" {...register('data_scadenza')} />
        </FieldWrap>
      </div>
      <FieldWrap label={t('richieste.omologa_form.note')} error={errors.note?.message}>
        <textarea className={inputCls} rows={2} {...register('note')} />
      </FieldWrap>

      <Controller
        name="trasportatori_ids"
        control={control}
        render={({ field }) => (
          <AnagraficaPicker
            label={t('richieste.omologa_form.trasportatori')}
            selectedIds={field.value ?? []}
            onChange={field.onChange}
            search={searchTras}
            onCreateNew={() => setShowCreaTras(true)}
            refreshKey={pickerRefresh}
          />
        )}
      />

      <Controller
        name="intermediari_ids"
        control={control}
        render={({ field }) => (
          <AnagraficaPicker
            label={t('richieste.omologa_form.intermediari')}
            selectedIds={field.value ?? []}
            onChange={field.onChange}
            search={searchInt}
            onCreateNew={() => setShowCreaInt(true)}
            refreshKey={pickerRefresh}
          />
        )}
      />

      {showCreaTras && (
        <CreaTrasportatoreInlineModal
          onClose={() => setShowCreaTras(false)}
          onCreated={handleTrasportatoreCreated}
        />
      )}
      {showCreaInt && (
        <CreaIntermediarioInlineModal
          onClose={() => setShowCreaInt(false)}
          onCreated={handleIntermediarioCreated}
        />
      )}
    </>
  );
}
